import { tryParseURL } from "../utils/url.ts";
import { normalizeText } from "./normalize-text.ts";

/**
 * Redaction attributes for analytics.
 *
 * A tracked click records the href and the label of the element. Some values
 * carry a secret. A prize win code is a URL whose path holds the token, and
 * the link shows that URL as its own label. Analytics must not store it.
 *
 * `data-track-redact-href` picks how much of the href survives.
 * `data-track-redact-text` replaces the label with a literal.
 *
 * Both attributes hold a literal. Never build one from the value it hides.
 */

const OPAQUE_ORIGIN = "null";

const DEFAULT_REDACTED_TEXT = "[redacted]";

/**
 * How much of an href survives.
 *
 * - `origin` keeps the scheme and host. Use it when the path holds the secret.
 * - `path` also keeps the path. Use it when the query holds the secret.
 * - `drop` records no href.
 *
 * An unknown value drops the href, so a typo cannot leak one.
 */
function redactHref(href: string, strategy: string): string | null {
  const url = tryParseURL(href);

  // An opaque origin, such as a deep link or a mailto link, redacts to the
  // string "null". That is useless in a report, so drop it instead.
  if (!url || url.origin === OPAQUE_ORIGIN) {
    return null;
  }

  if (strategy === "origin") {
    return url.origin;
  }

  if (strategy === "path") {
    return `${url.origin}${url.pathname}`;
  }

  // "drop", and any unknown value, records no href.
  return null;
}

/**
 * The href to record. Returns null when no href should be stored.
 *
 * Callers must classify the click on the raw href, not on this value. A
 * redacted href would otherwise change the event type.
 */
export function getRecordedHref(
  element: HTMLElement,
  rawHref: string | null,
): string | null {
  const strategy = element.dataset.trackRedactHref;

  if (strategy === undefined || rawHref === null) {
    return rawHref;
  }

  return redactHref(rawHref, strategy);
}

/**
 * The label to record.
 *
 * `data-track-redact-text` replaces the label. A bare attribute records
 * "[redacted]". `innerText` is never read for a redacted element, because it
 * is the value that needs hiding. The `aria-label` is skipped for the same
 * reason.
 */
export function getRecordedText(element: HTMLElement): string {
  const replacement = element.dataset.trackRedactText;

  if (replacement !== undefined) {
    return normalizeText(replacement) || DEFAULT_REDACTED_TEXT;
  }

  // Whitespace-only innerText is truthy. Normalize it before the fallback,
  // or such an element never reaches its aria-label.
  return (
    normalizeText(element.innerText) ||
    normalizeText(element.getAttribute("aria-label") ?? "")
  );
}
